/**
 * Workforce-Console view-model types.
 *
 * These are the shapes the UI components (CaseCard, AgentRowHeader,
 * WorkforceGrid, etc.) consume. Hooks under `./hooks/` produce them —
 * `useWorkforceCases` translates raw API case-records into `CaseItem`;
 * `useTeamAgents` produces the `Agent` row list.
 */

/** 3-tier SLA classification rendered as card background tint + agent counts. */
export type RiskLevel = 'healthy' | 'warning' | 'breached';

export interface Agent {
  id: string;
  name: string;
  initial: string;
  openCount: number;
  breachedCount: number;
  atRiskCount: number;
  online: boolean;
  /**
   * All teams this user belongs to within the current Workforce-Console
   * selection. Empty for the synthetic "Assigned to me" row.
   */
  teamIds: string[];
  /**
   * Identity used to draw the avatar — initials, deterministic color, and
   * email fallback. Only set for the synthetic "Assigned to me" row so the
   * avatar carries the real user's identity even though the row label stays
   * as "Assigned to me". For real member rows the avatar reads from `id` +
   * `name` directly.
   */
  avatarUserId?: string;
  avatarName?: string;
  avatarEmail?: string;
}

export interface CaseItem {
  id: string;
  payer: string;
  program: string;
  orgName: string;
  /**
   * Raw case-record state coming from the backend. Free-form string — there's
   * no enum on the API side. `CaseStatus` values are the canonical workflow
   * states we know about, but `status` is intentionally widened to `string`
   * so unknown states from new workflows still flow through — they land in the
   * catch-all `other` column until given a bucket in `WORKFORCE_COLUMNS`.
   */
  status: string;
  /** Human-readable label for `status` — resolved via `getStatusLabel` against
   *  the case's workflow `ui.status` config in the container. Always populated
   *  (falls back to pretty-formatted state when no config). */
  statusLabel: string;
  stageMinutes: number;
  slaDeltaMinutes: number;
  riskLevel: RiskLevel;
  /** case_record.workflow_state is PAUSED — the SLA clock is stopped. Drives
   *  the paused / running icon beside the card's SLA timer. */
  slaPaused: boolean;
  assigneeId: string;
  teamId: string;
  /** case_record.workflow_type — drives the "Pharm BV" / "Med BV" line on the card. */
  workflowType?: string;
  /** case_record.active_call_id — when present, routes `ai_call` / `human_call`
   *  cases into the "In Progress" columns instead of the "Needed" columns. */
  activeCallId?: string;
  /** case_record.status_flags.human_audit_in_progress — when true, routes an
   *  audit-needed case into "Human Audit In Progress" instead of "Human Audit
   *  Needed". */
  humanAuditInProgress?: boolean;
  /** case_record.program_id — used to build the case-details deep link. */
  programId?: string;
  /** case_record.organization_id — used to build the case-details deep link. */
  organizationId?: string;
  /** Human-readable disposition label resolved via `@100mslive/disposition-engine`
   *  against the case's `workflow_type`. Undefined when the workflow_type has
   *  no catalog (e.g. legacy `medbv`) or the code is missing — the UI omits
   *  the line in that case. */
  dispositionLabel?: string;
  /** Count of output fields still outstanding for a complete disposition.
   *  Undefined or `0` → chip is hidden. */
  missingFieldsCount?: number;
  /** Number of AI call records on the case. Undefined or `0` → chip is hidden. */
  aiCallAttempts?: number;
  /** Number of human call records on the case. Undefined or `0` → chip is hidden. */
  humanCallAttempts?: number;
  /** Payor slug the case's AI calls dial (`extractPayerHoursSlug`) — keys the
   *  clock popover's payer-hours fetch. Undefined when the case names none. */
  payerSlug?: string;
}

/** Synthetic agent id for the "Assigned to me" row at the top of the grid. */
export const ASSIGNED_TO_ME_ID = 'me';
